import { useSearchParams } from 'react-router-dom'
import { useCallback } from 'react'
import type { TransactionFilter } from '../types'

export interface ReconParams {
  clientId: string | null
  accountId: number | null
  statementId: string | null
  filter: TransactionFilter | null
  dateFrom: string | null
  dateTo: string | null
  search: string | null
  focusTransactionId: string | null
}

export function useReconParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const params: ReconParams = {
    clientId: searchParams.get('client') || null,
    accountId: searchParams.get('account') ? Number(searchParams.get('account')) : null,
    statementId: searchParams.get('statement') || null,
    filter: (searchParams.get('filter') as TransactionFilter) || null,
    dateFrom: searchParams.get('dateFrom') || null,
    dateTo: searchParams.get('dateTo') || null,
    search: searchParams.get('search') || null,
    focusTransactionId: searchParams.get('transaction') || null,
  }

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (value) {
          next.set(key, value)
        } else {
          next.delete(key)
        }
        return next
      }, { replace: true })
    },
    [setSearchParams]
  )

  const setClient = useCallback(
    (id: string | null) => {
      setSearchParams(() => {
        const next = new URLSearchParams()
        if (id) next.set('client', id)
        return next
      }, { replace: true })
    },
    [setSearchParams]
  )

  const setAccount = useCallback(
    (id: number | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (id != null) {
          next.set('account', id.toString())
        } else {
          next.delete('account')
        }
        next.delete('statement')
        next.delete('filter')
        next.delete('dateFrom')
        next.delete('dateTo')
        next.delete('search')
        return next
      }, { replace: true })
    },
    [setSearchParams]
  )

  const setStatement = useCallback(
    (id: string | null) => setParam('statement', id),
    [setParam]
  )

  // Set the account without clearing the statement — used for deep-link
  // resolution where the statement was specified by URL and we want to keep it.
  const setAccountKeepStatement = useCallback(
    (id: number | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (id != null) next.set('account', id.toString())
        else next.delete('account')
        return next
      }, { replace: true })
    },
    [setSearchParams]
  )

  const setFilter = useCallback(
    (f: TransactionFilter | null) => setParam('filter', f),
    [setParam]
  )

  const setDateRange = useCallback(
    (from: string | null, to: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (from) next.set('dateFrom', from); else next.delete('dateFrom')
        if (to) next.set('dateTo', to); else next.delete('dateTo')
        return next
      }, { replace: true })
    },
    [setSearchParams]
  )

  const setSearch = useCallback(
    (s: string | null) => setParam('search', s || null),
    [setParam]
  )

  return {
    params,
    setClient,
    setAccount,
    setAccountKeepStatement,
    setStatement,
    setFilter,
    setDateRange,
    setSearch,
  }
}
